import type { StaffMemoryFact, StaffMemoryReviewItem } from "@lafayette/schemas";
import { useId } from "react";

import { Button } from "../../ui/button";
import { Compare } from "../components/Compare";
import { DetailLink, FactContext, FactMeta, nameWith, WarningNote } from "../components/parts";
import { EmptyNote, FactList, MemorySection } from "./FactSections";

export type ReviewAction = "accept" | "activate" | "reject";

type Props = Readonly<{
  items: readonly StaffMemoryReviewItem[];
  customerId: string;
  now: Date;
  canManage: boolean;
  onAction: (action: ReviewAction, item: StaffMemoryReviewItem) => void;
}>;

/**
 * The challenged fact as the page may rely on it. The real view returns `challenges` for a fact of ANY status
 * it holds (another candidate, or a replaced or deleted fact when the history was read), not only the ACTIVE or
 * DISPUTED fact the contract note (3.3) describes. Only an ACTIVE or DISPUTED fact is a memory the candidate
 * would replace, so only that one is shown as "Current memory" and can be accepted over.
 */
export function openChallenge(item: StaffMemoryReviewItem): StaffMemoryFact | null {
  const challenged = item.challenges;
  return challenged !== null && (challenged.status === "ACTIVE" || challenged.status === "DISPUTED")
    ? challenged
    : null;
}

/**
 * What a review item may do:
 *  - a candidate that challenges an ACTIVE or DISPUTED fact is accepted (it replaces that fact) or rejected; the
 *    API refuses to activate it alone (`memory.transition_invalid`);
 *  - any other candidate is activated or rejected. That includes a candidate whose challenged fact is another
 *    candidate, a replaced or deleted fact, or a fact outside this page. The server refuses the activation with
 *    a 409 when an open challenged fact still exists (the dialog explains it), so the page never has to guess,
 *    and the extraction evidence of the candidate is never lost because only Reject was offered.
 */
export function reviewActions(item: StaffMemoryReviewItem): readonly ReviewAction[] {
  return openChallenge(item) !== null ? ["accept", "reject"] : ["activate", "reject"];
}

const ACTION_LABEL: Readonly<Record<ReviewAction, string>> = {
  accept: "Accept candidate",
  activate: "Activate",
  reject: "Reject",
};

function ReviewItem({
  item,
  customerId,
  now,
  canManage,
  onAction,
  position,
}: Readonly<
  Omit<Props, "items"> & { item: StaffMemoryReviewItem; position: { index: number; total: number } }
>) {
  const valueId = useId();
  const contextId = useId();
  const baseId = useId();
  const { candidate } = item;
  const current = openChallenge(item);
  return (
    <li className="grid min-w-0 gap-3 rounded-md border border-hairline bg-card p-4">
      <FactContext
        id={contextId}
        kind={candidate.kind}
        position={{ noun: "candidate", ...position }}
        visible
      />
      <Compare current={current} candidate={candidate} now={now} candidateValueId={valueId} />
      {candidate.conflictsWithFactId !== null && current === null ? (
        <WarningNote role="note">
          This candidate points at a memory that is not active or disputed on this page: another
          candidate, a memory that was replaced or deleted, or one this page does not hold. You can
          activate or reject it. The server refuses to activate it while an open memory it
          challenges still exists.
        </WarningNote>
      ) : null}
      <FactMeta fact={candidate} now={now} compact />
      <div className="flex flex-wrap gap-2 max-sm:[&>*]:flex-1">
        <DetailLink
          customerId={customerId}
          fact={candidate}
          describedBy={valueId}
          contextId={contextId}
        />
        {canManage
          ? reviewActions(item).map((action) => {
              const ownId = `${baseId}-${action}`;
              return (
                <Button
                  key={action}
                  id={ownId}
                  size="sm"
                  variant={action === "reject" ? "danger" : "default"}
                  aria-describedby={valueId}
                  aria-labelledby={nameWith(ownId, contextId)}
                  onClick={() => onAction(action, item)}
                >
                  {ACTION_LABEL[action]}
                </Button>
              );
            })
          : null}
      </div>
    </li>
  );
}

export function ReviewSection({ items, customerId, now, canManage, onAction }: Props) {
  return (
    <MemorySection
      id="memory-review"
      className="gap-[18px]"
      title={`Needs review (${items.length})`}
      description="Candidates wait for a person. Lafayette does not use a candidate until you accept or activate it. Read where each one came from before you decide."
    >
      {items.length === 0 ? (
        <EmptyNote>Nothing needs review.</EmptyNote>
      ) : (
        <FactList>
          {items.map((item, index) => (
            <ReviewItem
              key={item.candidate.id}
              item={item}
              customerId={customerId}
              now={now}
              canManage={canManage}
              onAction={onAction}
              position={{ index: index + 1, total: items.length }}
            />
          ))}
        </FactList>
      )}
    </MemorySection>
  );
}
